import { useCallback, useEffect, useState } from 'react';
import { staff as staffApi, roles as rolesApi, stores as storesApi } from '../api/endpoints';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { Loading, Empty, Modal, Can, Pagination, dateTime } from '../components/Common';

/**
 * Staff accounts. This is where the Super Admin actually hands out a login:
 * pick a Role (which carries the permissions), optionally pin the account to a
 * single store, set a password.
 */
export default function Staff() {
  const toast = useToast();
  const { isSuperAdmin, admin } = useAuth();

  const [data, setData] = useState({ staff: [], meta: null });
  const [roleList, setRoleList] = useState([]);
  const [storeList, setStoreList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await staffApi.list({ page, limit: 20, search: search || undefined });
      setData(res.data);
    } catch (err) {
      toast.error('Could not load staff', err.message);
    } finally {
      setLoading(false);
    }
  }, [page, search, toast]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    rolesApi.list().then((res) => setRoleList(res.data.roles)).catch(() => {});
    if (isSuperAdmin) storesApi.list({ limit: 100 }).then((res) => setStoreList(res.data.stores)).catch(() => {});
    document.title = 'Staff · UrbanClean';
  }, [isSuperAdmin]);

  const toggle = async (member) => {
    try {
      const res = await staffApi.toggle(member._id);
      toast.success(res.message);
      load();
    } catch (err) {
      toast.error('Could not update the account', err.message);
    }
  };

  const remove = async (member) => {
    if (!window.confirm(`Delete the login for ${member.name}?`)) return;
    try {
      await staffApi.remove(member._id);
      toast.success('Staff account deleted');
      load();
    } catch (err) {
      toast.error('Could not delete the account', err.message);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Staff</h1>
          <div className="sub">Create logins and scope them with a role</div>
        </div>
        <Can permission="staff.create">
          <button className="btn btn-blue" onClick={() => setEditing({})}>+ New staff login</button>
        </Can>
      </div>

      <div className="toolbar">
        <input
          className="input"
          placeholder="Search name, email or mobile"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          style={{ minWidth: 250 }}
        />
      </div>

      <div className="card">
        {loading ? <Loading /> : data.staff.length === 0 ? (
          <Empty title="No staff accounts yet" body="Create one and pick the role it should have." />
        ) : (
          <>
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr><th>Name</th><th>Role</th><th>Store</th><th>Status</th><th>Last sign-in</th><th></th></tr>
                </thead>
                <tbody>
                  {data.staff.map((member) => (
                    <tr key={member._id}>
                      <td>
                        <div className="strong">{member.name}</div>
                        <div className="muted" style={{ fontSize: 12 }}>{member.email}</div>
                        {member.designation ? <div className="muted" style={{ fontSize: 11.5 }}>{member.designation}</div> : null}
                      </td>
                      <td>
                        {member.isSuperAdmin ? (
                          <span className="badge badge-blue">Super Admin</span>
                        ) : member.role ? (
                          <>
                            <span className="badge badge-gray">{member.role.name}</span>
                            <div className="muted" style={{ fontSize: 11.5, marginTop: 3 }}>
                              {member.role.permissions?.length || 0} permissions
                            </div>
                          </>
                        ) : <span className="muted">No role</span>}
                      </td>
                      <td className="muted">{member.store?.name || 'All stores'}</td>
                      <td>
                        <span className={`badge badge-${member.isActive ? 'green' : 'red'}`}>
                          {member.isActive ? 'Active' : 'Disabled'}
                        </span>
                      </td>
                      <td className="muted nowrap">{member.lastLoginAt ? dateTime(member.lastLoginAt) : 'Never'}</td>
                      <td className="right nowrap">
                        <Can permission="staff.update">
                          <button className="btn btn-sm btn-ghost" onClick={() => setEditing(member)}>Edit</button>
                          {!member.isSuperAdmin && member._id !== admin?._id ? (
                            <button className="btn btn-sm btn-ghost" style={{ marginLeft: 6 }} onClick={() => toggle(member)}>
                              {member.isActive ? 'Disable' : 'Enable'}
                            </button>
                          ) : null}
                        </Can>
                        {!member.isSuperAdmin && member._id !== admin?._id ? (
                          <Can permission="staff.delete">
                            <button className="btn btn-sm btn-ghost" style={{ marginLeft: 6, color: 'var(--red)' }} onClick={() => remove(member)}>
                              Delete
                            </button>
                          </Can>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination meta={data.meta} onPage={setPage} />
          </>
        )}
      </div>

      <StaffEditor
        member={editing}
        roleList={roleList}
        storeList={storeList}
        isSuperAdmin={isSuperAdmin}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    </>
  );
}

function StaffEditor({ member, roleList, storeList, isSuperAdmin, onClose, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!member) return;
    setForm({
      name: member.name || '',
      email: member.email || '',
      mobile: member.mobile || '',
      password: '',
      role: member.role?._id || member.role || '',
      store: member.store?._id || member.store || '',
      designation: member.designation || '',
      employmentType: member.employmentType || 'full-time',
    });
  }, [member]);

  if (!member) return null;
  const isNew = !member._id;

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = async () => {
    if (!form.name || !form.email) return toast.error('Name and email are required');
    if (isNew && !form.password) return toast.error('Set a password for the new login');

    setBusy(true);
    try {
      const body = { ...form };
      if (!body.password) delete body.password;
      if (!body.role) body.role = null;
      if (!body.store) body.store = null;

      if (isNew) await staffApi.create(body);
      else await staffApi.update(member._id, body);

      toast.success(isNew ? 'Staff login created' : 'Staff account updated');
      onSaved();
    } catch (err) {
      toast.error('Could not save', err.message);
    } finally {
      setBusy(false);
    }
  };

  const selectedRole = roleList.find((r) => r._id === form.role);

  return (
    <Modal
      open
      title={isNew ? 'New staff login' : `Edit — ${member.name}`}
      onClose={onClose}
      footer={<>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy}>
          {busy ? 'Saving…' : isNew ? 'Create login' : 'Save changes'}
        </button>
      </>}
    >
      <div className="row">
        <div className="field">
          <label>Full name *</label>
          <input className="input" value={form.name || ''} onChange={set('name')} />
        </div>
        <div className="field">
          <label>Mobile</label>
          <input className="input" value={form.mobile || ''} onChange={set('mobile')} />
        </div>
      </div>

      <div className="field">
        <label>Email (this is their username) *</label>
        <input className="input" type="email" value={form.email || ''} onChange={set('email')} />
      </div>

      <div className="field">
        <label>{isNew ? 'Password *' : 'New password'}</label>
        <input
          className="input"
          type="password"
          value={form.password || ''}
          onChange={set('password')}
          placeholder={isNew ? 'At least 6 characters' : 'Leave blank to keep the current password'}
        />
      </div>

      <div className="row">
        <div className="field">
          <label>Role</label>
          <select className="select" value={form.role || ''} onChange={set('role')} disabled={member.isSuperAdmin}>
            <option value="">No role (no access)</option>
            {roleList.map((r) => <option key={r._id} value={r._id}>{r.name}</option>)}
          </select>
          {selectedRole ? (
            <div className="hint">
              Grants {selectedRole.permissions.length} permission(s): {selectedRole.permissions.slice(0, 5).join(', ')}
              {selectedRole.permissions.length > 5 ? '…' : ''}
            </div>
          ) : (
            <div className="hint">Without a role this login can sign in but reach nothing.</div>
          )}
        </div>

        {isSuperAdmin ? (
          <div className="field">
            <label>Store</label>
            <select className="select" value={form.store || ''} onChange={set('store')}>
              <option value="">All stores (head office)</option>
              {storeList.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
            </select>
            <div className="hint">Pinning to a store limits this account to that branch's data.</div>
          </div>
        ) : null}
      </div>

      <div className="row">
        <div className="field">
          <label>Designation</label>
          <input className="input" value={form.designation || ''} onChange={set('designation')} placeholder="e.g. Counter Staff" />
        </div>
        <div className="field">
          <label>Employment type</label>
          <select className="select" value={form.employmentType || 'full-time'} onChange={set('employmentType')}>
            <option value="full-time">Full-time</option>
            <option value="part-time">Part-time</option>
            <option value="contract">Contract</option>
          </select>
        </div>
      </div>
    </Modal>
  );
}
